import { View, Text } from '@react-pdf/renderer';
import {
  AssessmentAnswer,
  AssessmentQuestionType,
  MultipleChoiceAssessmentAnswer,
  MultiSelectAssessmentAnswer,
  FreeTextAssessmentAnswer,
  InitialsAssessmentAnswer,
  SignatureAssessmentAnswer,
  MultiSignatureAssessmentAnswer,
  FileUploadAssessmentAnswer,
  PatientContactAssessmentAnswer,
} from '../../../../generated/graphql';
import { getFullDateTimeStamp } from '../../../lib/time';
import { getAnswerValue } from '../../../lib/answerHelpers';
import { isMultiSelectAssessmentQuestion } from '../../../lib/assessments';
import { getMultiSelectDisplayRows } from '../../../lib/answer-option-display';
import { ProgramAssessmentQuestion } from '../../../lib/program-assessment';
import { colors } from '../../../lib/colors';
import PdfFormattedText from '../../Library/LibraryFormPDF/components/PdfFormattedText';
import CompletedSignature from './components/CompletedSignature';
import { styles } from '.';
import { formatPatientContactSnapshotAnswer } from '../../../lib/patientContactLabels';

export const getAnswerContent = (
  answer: AssessmentAnswer | undefined,
  templateQuestion?: ProgramAssessmentQuestion | null,
): React.ReactNode => {
  if (!answer) return <></>;

  switch (answer.questionType) {
    case AssessmentQuestionType.MultipleChoiceScored:
    case AssessmentQuestionType.MultipleChoice: {
      const multipleChoiceAnswer = answer as MultipleChoiceAssessmentAnswer;
      return (
        <Text style={styles.answerText}>
          {multipleChoiceAnswer.answer ?? 'No answer provided'}
        </Text>
      );
    }

    case AssessmentQuestionType.MultiSelect: {
      const multiSelectAnswer = answer as MultiSelectAssessmentAnswer;
      const optionLabels =
        templateQuestion && isMultiSelectAssessmentQuestion(templateQuestion)
          ? templateQuestion.answerOptions?.map(
              (option) => option.answerOption,
            ) ?? []
          : [];
      const optionRows = getMultiSelectDisplayRows(
        optionLabels,
        multiSelectAnswer.answers,
      );

      if (!optionRows.length) {
        return <Text style={styles.answerText}>Nothing selected</Text>;
      }

      return (
        <View style={{ marginTop: 4 }}>
          {optionRows.map((row, index) => {
            if (row.kind === 'header') {
              return (
                <View key={index} style={{ marginTop: 6, marginBottom: 2 }}>
                  <PdfFormattedText
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: colors.neutral[150],
                    }}
                  >
                    {row.label}
                  </PdfFormattedText>
                </View>
              );
            }

            return (
              <View
                key={index}
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  marginBottom: 4,
                }}
              >
                <View
                  style={{
                    width: 9,
                    height: 9,
                    borderWidth: 1,
                    borderColor: colors.neutral[150],
                    marginRight: 6,
                    marginTop: 2,
                    backgroundColor: row.selected
                      ? colors.neutral[150]
                      : 'transparent',
                  }}
                />
                <View style={{ flex: 1 }}>
                  <PdfFormattedText style={styles.answerText}>
                    {row.label}
                  </PdfFormattedText>
                </View>
              </View>
            );
          })}
        </View>
      );
    }

    case AssessmentQuestionType.FreeText:
    case AssessmentQuestionType.RichText: {
      const textAnswer = answer as FreeTextAssessmentAnswer;
      return (
        <Text style={styles.answerText}>
          {textAnswer.answer ?? 'No answer provided'}
        </Text>
      );
    }

    case AssessmentQuestionType.Initials: {
      const initialsAnswer = answer as InitialsAssessmentAnswer;
      return initialsAnswer.signatureName ? (
        <View style={{ marginTop: 2 }}>
          <CompletedSignature
            name={initialsAnswer.signatureName}
            role="Client"
            date={getFullDateTimeStamp(initialsAnswer?.signedAt)}
            hideNameAndRole={true}
            hideDate={false}
          />
        </View>
      ) : (
        <Text style={styles.answerText}>Not initialed</Text>
      );
    }

    case AssessmentQuestionType.Signature: {
      const signatureAnswer = answer as SignatureAssessmentAnswer;
      return signatureAnswer.signatureName ? (
        <View style={{ marginTop: 4 }}>
          <CompletedSignature
            name={signatureAnswer.signatureName}
            role="Client"
            date={getFullDateTimeStamp(signatureAnswer.signedAt)}
            hideNameAndRole={false}
            hideDate={false}
          />
        </View>
      ) : (
        <Text style={styles.answerText}>Not signed</Text>
      );
    }

    case AssessmentQuestionType.MultiSignature: {
      const multiSignatureAnswer = answer as MultiSignatureAssessmentAnswer;
      return multiSignatureAnswer.signatures?.length ? (
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 8,
            marginTop: 16,
          }}
        >
          {multiSignatureAnswer.signatures.map((signature, index) => (
            <View
              key={index}
              style={{
                flexBasis: '25%',
                marginBottom: 8,
              }}
            >
              <CompletedSignature
                name={signature.signatureName}
                role={signature.signatureRole || 'Client'}
                date={getFullDateTimeStamp(signature.signedAt)}
                hideNameAndRole={false}
                hideDate={false}
              />
            </View>
          ))}
        </View>
      ) : (
        <Text style={styles.answerText}>Not signed</Text>
      );
    }

    case AssessmentQuestionType.Autofill:
    case AssessmentQuestionType.DatePicker: {
      const answerValue = getAnswerValue(answer);
      return (
        <Text style={styles.answerText}>
          {answerValue ?? 'No answer provided'}
        </Text>
      );
    }

    case AssessmentQuestionType.FileUpload: {
      const fileUploadAnswer = answer as FileUploadAssessmentAnswer;
      if (fileUploadAnswer.fileName) {
        return (
          <Text style={styles.answerText}>{fileUploadAnswer.fileName}</Text>
        );
      }
      return <Text style={styles.answerText}>No file uploaded</Text>;
    }

    case AssessmentQuestionType.PatientContact: {
      const pc = answer as PatientContactAssessmentAnswer;
      const text = formatPatientContactSnapshotAnswer(
        pc.contactSnapshot,
      ).replace(/\*\*/g, '');
      return <Text style={styles.answerText}>{text}</Text>;
    }

    default:
      return <Text style={styles.answerText}>Unsupported answer type</Text>;
  }
};
